import { Menu, X } from 'lucide-react'
import React, { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { AuthContext } from '../../contexts/auth-context'
import AuthModal from '../modals/auth-modal'
import TrialModal from '../stripe-components/TrialModal'
import { Button } from '../ui/button'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu'
import { navigateCMSpage, navigatetoHome } from '@/utils/footer-navigations'
import logger from '../../utils/logger'
import { getBusinessSubscriptionService } from '../../services'

function Header() {
  const [isTrialModalOpen, setIsTrialModalOpen] = useState(false)
  const [hasUsedTrialBefore, setHasUsedTrialBefore] = useState(false)
  const [menuOpen, setMenuOpen] = useState(false)
  const { isLoginModalOpen, setIsLoginModalOpen, auth, resetCredentials, setModalContent } =
    useContext(AuthContext)
  const navigate = useNavigate()

  const isProfileIncomplete = () => {
    const requiredFields = [
      'name',
      'address',
      'city',
      'state',
      'postal_code',
      'country',
      'gender',
      'dob',
    ]
    if (!auth?.user) return false

    return requiredFields.some((field) => {
      const val = auth.user[field]
      return val === undefined || val === null || val === ''
    })
  }

  const handleLoginButtonClick = () => {
    if (!isLoginModalOpen) {
      setIsLoginModalOpen(true)
    }
    setModalContent(<AuthModal />)
  }

  const fetchAllSubscription = async () => {
    try {
      const response = await getBusinessSubscriptionService()

      // The response.data.data is directly the array of subscription objects you provided
      const allSubscriptions = response.data.data

      // Filter subscriptions where trialStart and trialEnd are not null
      const subscriptionsWithTrials = allSubscriptions.filter(
        (subscription) => subscription.trialStart !== null && subscription.trialEnd !== null
      )

      if (subscriptionsWithTrials.length > 0 && subscriptionsWithTrials) {
        setHasUsedTrialBefore(true)
      }
    } catch (error) {
      logger.error(error)
    }
  }

  useEffect(() => {
    fetchAllSubscription()
  }, [])

  const handleHomeButtonclick = () => {
    // Check if the user is a Business and their subscription is not active or about to be canceled
    if (
      auth?.user?.role === 'Business' &&
      (!auth?.user?.subscription ||
        auth?.user?.subscription?.status !== 'active' ||
        auth?.user?.subscription?.status === 'triling' ||
        auth?.user?.subscription?.cancelAtPeriodEnd)
    ) {
      navigate('/business/profile')
      return
    }

    if (window.location.pathname.includes('/user')) {
      navigate('/')
      return
    }

    if (auth.user.role === 'user') {
      navigate('/user')
    } else if (auth.user.role === 'Business') {
      navigate('/business')
    }
  }

  const handleLogout = () => {
    localStorage.removeItem('accessToken')
    resetCredentials()
  }

  // replace setShowCardInput(true) with this:
  const handleStartTrialClick = () => {
    setIsTrialModalOpen(true)
  }

  // Determine if the "Start trial" button should be shown
  const showStartFreeButton =
    auth?.user?.role === 'Business' &&
    !hasUsedTrialBefore &&
    (!auth?.user?.subscription ||
      auth?.user?.subscription?.status !== 'active' ||
      auth?.user?.subscription?.status === 'triling')

  return (
    <div className="bg-gradient-to-r from-[#F7E5FF] via-[#F7E6FF] via-10% to-[#D6E0FF] to-100% w-full h-20">
      <div className="w-full h-full flex justify-between items-center content-center px-4 sm:px-6 xl:w-7xl m-auto py-3 relative ">
        {/* Trial Modal - stripe modal that take the card info */}
        <TrialModal open={isTrialModalOpen} onClose={() => setIsTrialModalOpen(false)} />
        {/* Logo - no more mb-4 or mt-21 to keep it aligned properly */}
        <div className="z-1">
          <img
            onClick={handleHomeButtonclick}
            src="/Logo.svg"
            alt="Logo"
            className={`transition-all duration-200
      h-12 w-12 lg:h-auto lg:w-auto 
      ${isProfileIncomplete() ? 'sm:h-10 sm:w-10 sm:mb-0 md:h-10 md:w-10 md:mb-0 lg:pt-24' : 'sm:mb-[-20px] md:mb-[-80px] md:w-auto md:h-auto'}
    `}
          />
        </div>

        {/* Hamburger Icon for mobile - now directly in the flex layout */}
        <div className="sm:hidden">
          <button onClick={() => setMenuOpen(!menuOpen)}>
            {menuOpen ? <X size={26} /> : <Menu size={26} />}
          </button>
        </div>

        {/* Navigation items */}
        <div
          className={`transition-all duration-300 ease-in-out sm:flex sm:justify-between sm:w-auto ${
            menuOpen
              ? 'block absolute top-20 left-4 right-4 bg-white shadow-lg rounded-2xl p-6 z-50 border border-gray-100'
              : 'hidden'
          } sm:block sm:static sm:bg-transparent sm:shadow-none sm:p-0 sm:border-none sm:rounded-none`}
        >
          <ul className="flex flex-col sm:flex-row sm:gap-8 gap-6 items-center text-base font-medium text-heading sm:ml-auto">
            <li
              className={`w-full sm:w-auto flex justify-center ${auth?.authenticate ? '' : 'hidden'}`}
            >
              <Button
                onClick={() => {
                  handleHomeButtonclick()
                  setMenuOpen(false)
                }}
                className="rounded-xl bg-button-purple text-white hover:bg-white hover:text-button-purple border border-transparent hover:border-button-purple font-semibold transition-all duration-200 w-full sm:w-auto"
              >
                Dashboard
              </Button>
            </li>
            <li
              onClick={() => navigatetoHome('/#services')}
              className="cursor-pointer hover:text-button-purple transition-colors duration-200 w-full sm:w-auto text-center"
            >
              Services
            </li>
            <li
              onClick={() => navigateCMSpage('about-us')}
              className="cursor-pointer hover:text-button-purple transition-colors duration-200 w-full sm:w-auto text-center"
            >
              About Us
            </li>
            <li
              onClick={() => navigatetoHome('/#prices')}
              className="cursor-pointer hover:text-button-purple transition-colors duration-200 w-full sm:w-auto text-center"
            >
              Pricing
            </li>
            <li
              onClick={() => navigatetoHome('/contact-us')}
              className="cursor-pointer hover:text-button-purple transition-colors duration-200 w-full sm:w-auto text-center"
            >
              Contact Us
            </li>

            <li className="w-full sm:w-auto flex justify-center">
              {auth?.user ? (
                <>
                  {showStartFreeButton && (
                    <Button
                      onClick={() => {
                        handleStartTrialClick()
                        setMenuOpen(false)
                      }}
                      className="cursor-pointer rounded-xl bg-[#4c2859] text-white
                                 text-sm px-4 py-2 mr-4 font-semibold shadow-md
                                 hover:bg-[#643d73] hover:scale-105 transform transition-all duration-300
                                 focus:ring-4 focus:ring-purple-300"
                    >
                      ✨ Start Free Trial ✨
                    </Button>
                  )}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <div className="flex items-center gap-2 cursor-pointer">
                        <Avatar className="cursor-pointer border-2 border-transparent hover:border-button-purple transition-all duration-200">
                          <AvatarImage
                            src={
                              auth?.user?.profile_image
                                ? `${import.meta.env.VITE_BASE_URL}${auth?.user?.profile_image}`
                                : '/profile.svg'
                            }
                          />
                          <AvatarFallback>
                            {auth?.user?.name?.[0]?.toUpperCase() || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        {auth?.user?.role === 'user' && (
                          <span className="text-sm font-medium">{auth?.user?.name}</span>
                        )}
                      </div>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-40">
                      <DropdownMenuItem
                        className=" cursor-pointer text-blue-500"
                        onClick={() => {
                          if (auth?.user?.role === 'user') {
                            navigate('/user/profile')
                          } else {
                            navigate('/business/profile')
                          }
                          setMenuOpen(false)
                        }}
                      >
                        Profile
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        onClick={() => {
                          handleLogout()
                          setMenuOpen(false)
                        }}
                        className="text-red-500  cursor-pointer"
                      >
                        Logout
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : (
                <Button
                  onClick={() => {
                    handleLoginButtonClick()
                    setMenuOpen(false)
                  }}
                  className="rounded-xl bg-button-purple text-white hover:bg-white hover:text-button-purple border border-transparent hover:border-button-purple font-semibold transition-all duration-200 w-full sm:w-auto"
                >
                  Login/Register
                </Button>
              )}
            </li>
          </ul>
        </div>
      </div>
    </div>
  )
}

export default Header
